import { useState, useEffect } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Loader2, Download, RotateCcw, Users, Mail, Building2, MapPinned } from "lucide-react";
import { toast } from "sonner";
import EstrategiasPanel from "./EstrategiasPanel";
import EmpresasPanel from "./EmpresasPanel";
import SolicitudesPanel from "./SolicitudesPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ARCHETYPE_LABEL = {
  leon: "LEÓN",
  lobo: "LOBO",
  halcon: "HALCÓN",
  delfin: "DELFÍN",
};
const ARCHETYPE_COLOR = {
  leon: "#FF6B4A",
  lobo: "#A78BFA",
  halcon: "#29BC9B",
  delfin: "#5BC0EB",
};
const STORAGE_KEY = "pyg.admin.pwd";

export default function AdminPage() {
  const [pwd, setPwd] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [view, setView] = useState("leads"); // 'leads' | 'sequences' | 'companies'

  useEffect(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) {
      setPwd(saved);
      tryLogin(saved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tryLogin = async (password) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/leads`, {
        headers: { "X-Admin-Password": password },
      });
      setData(data);
      setAuthed(true);
      sessionStorage.setItem(STORAGE_KEY, password);
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Contraseña incorrecta");
        sessionStorage.removeItem(STORAGE_KEY);
      } else {
        toast.error("No pudimos cargar los leads");
      }
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (!pwd.trim()) return;
    tryLogin(pwd.trim());
  };

  const logout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setAuthed(false);
    setPwd("");
    setData(null);
  };

  const exportCsv = () => {
    if (!data?.leads) return;
    const rows = [
      ["id", "name", "email", "archetype", "secondary", "leon", "lobo", "halcon", "delfin", "company_name", "company_code", "click_mapa_completo", "solicito_mapa_completo", "whatsapp_mapa", "origen_interes_mapa", "fecha_solicitud_mapa", "created_at"],
      ...filtered.map((l) => [
        l.id,
        l.name,
        l.email,
        l.archetype,
        l.secondary || "",
        l.scores?.leon ?? 0,
        l.scores?.lobo ?? 0,
        l.scores?.halcon ?? 0,
        l.scores?.delfin ?? 0,
        l.company_name || "",
        l.company_code || "",
        l.click_mapa_completo ? "true" : "false",
        l.solicito_mapa_completo ? "true" : "false",
        l.whatsapp_mapa || "",
        l.origen_interes_mapa || "",
        l.fecha_solicitud_mapa || "",
        l.created_at,
      ]),
    ];
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const tag = companyFilter !== "all" ? `-${companyFilter}` : "";
    a.download = `pyg-leads${tag}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered =
    data?.leads?.filter((l) => {
      if (filter !== "all" && l.archetype !== filter) return false;
      if (companyFilter === "all") return true;
      if (companyFilter === "__none__") return !l.company_id;
      return l.company_code === companyFilter;
    }) || [];

  // List of unique companies present in leads, for the filter dropdown
  const companyOptions = (() => {
    const seen = new Map();
    (data?.leads || []).forEach((l) => {
      if (l.company_code && l.company_name && !seen.has(l.company_code)) {
        seen.set(l.company_code, l.company_name);
      }
    });
    return Array.from(seen.entries()).map(([code, name]) => ({ code, name }));
  })();

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-4">
        <form
          onSubmit={handleLogin}
          className="w-full max-w-sm space-y-6 p-8 rounded-md border border-white/8 bg-[#0A0A0A]"
          data-testid="admin-login-form"
        >
          <div className="space-y-2 text-center">
            <Lock className="w-6 h-6 text-[#29BC9B] mx-auto" />
            <h1 className="font-display text-2xl">Acceso administrador</h1>
            <p className="text-xs text-white/40">PLAY YOUR GAME™</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="pwd" className="text-[10px] uppercase tracking-[0.3em] text-white/60">
              Contraseña
            </Label>
            <Input
              id="pwd"
              data-testid="admin-pwd-input"
              type="password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              placeholder="••••••••"
              className="bg-[#050505] border-white/10 text-white h-12 rounded-sm focus-visible:ring-[#29BC9B]"
            />
          </div>
          <button
            type="submit"
            data-testid="admin-login-btn"
            disabled={loading}
            className="btn-teal w-full justify-center disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Entrar"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/8 px-8 py-5 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#29BC9B]">
            PLAY YOUR GAME™
          </div>
          <h1 className="font-display text-xl">
            Dashboard · {view === "leads" ? "Leads" : view === "sequences" ? "Estrategias bajo presión" : view === "companies" ? "Empresas" : "Solicitudes"}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          {view === "leads" && (
            <button onClick={exportCsv} className="btn-ghost !py-2 !px-4 !text-[10px]" data-testid="admin-export-csv">
              <Download className="w-3.5 h-3.5" /> CSV
            </button>
          )}
          <button onClick={() => tryLogin(pwd)} className="btn-ghost !py-2 !px-4 !text-[10px]" data-testid="admin-refresh-btn">
            <RotateCcw className="w-3.5 h-3.5" /> Refrescar
          </button>
          <button onClick={logout} className="btn-ghost !py-2 !px-4 !text-[10px]" data-testid="admin-logout-btn">
            Salir
          </button>
        </div>
      </header>

      {/* Tabs */}
      <nav className="border-b border-white/8 px-8 flex items-center gap-1">
        {[
          { id: "leads", label: "Leads", icon: Users },
          { id: "sequences", label: "Estrategias", icon: Mail },
          { id: "companies", label: "Empresas", icon: Building2 },
          { id: "solicitudes", label: "Solicitudes", icon: MapPinned },
        ].map((t) => {
          const Icon = t.icon;
          const active = view === t.id;
          return (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setView(t.id)}
              className={`flex items-center gap-2 px-4 py-3 text-[11px] uppercase tracking-[0.25em] border-b-2 -mb-px transition-colors ${
                active
                  ? "border-[#29BC9B] text-[#29BC9B]"
                  : "border-transparent text-white/40 hover:text-white/80"
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          );
        })}
      </nav>

      <main className="px-8 py-10 max-w-7xl mx-auto space-y-10">
        {view === "leads" && (
          <>
            {/* Stats */}
            <section className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <StatCard label="Total" value={data?.total ?? 0} accent="#29BC9B" />
              {["leon", "lobo", "halcon", "delfin"].map((c) => (
                <StatCard
                  key={c}
                  label={ARCHETYPE_LABEL[c]}
                  value={data?.counts_by_archetype?.[c] ?? 0}
                  accent={ARCHETYPE_COLOR[c]}
                />
              ))}
            </section>

            {/* Filter */}
            <section className="flex flex-wrap items-center gap-2">
              <span className="text-[10px] uppercase tracking-[0.3em] text-white/40 mr-2">
                Filtrar
              </span>
              {["all", "leon", "lobo", "halcon", "delfin"].map((c) => (
                <button
                  key={c}
                  data-testid={`admin-filter-${c}`}
                  onClick={() => setFilter(c)}
                  className={`px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.2em] border transition-colors ${
                    filter === c
                      ? "border-[#29BC9B] text-[#29BC9B] bg-[#29BC9B]/5"
                      : "border-white/10 text-white/50 hover:border-white/30"
                  }`}
                >
                  {c === "all" ? "Todos" : ARCHETYPE_LABEL[c]}
                </button>
              ))}
              <span className="mx-2 h-4 w-px bg-white/10" />
              <select
                data-testid="admin-filter-company"
                value={companyFilter}
                onChange={(e) => setCompanyFilter(e.target.value)}
                className="bg-[#0A0A0A] border border-white/10 rounded-full px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-white/70 focus:outline-none focus:border-[#29BC9B] hover:border-white/30 transition-colors"
              >
                <option value="all">Todas las empresas</option>
                <option value="__none__">Sin empresa</option>
                {companyOptions.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name} ({c.code})
                  </option>
                ))}
              </select>
              <span className="text-xs text-white/40 ml-auto">{filtered.length} resultados</span>
            </section>

            {/* Table */}
            <section className="rounded-md border border-white/8 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-leads-table">
                  <thead className="bg-[#0A0A0A] text-[10px] uppercase tracking-[0.2em] text-white/40">
                    <tr>
                      <th className="text-left px-4 py-3">Nombre</th>
                      <th className="text-left px-4 py-3">Email</th>
                      <th className="text-left px-4 py-3">Empresa</th>
                      <th className="text-left px-4 py-3">Dominante</th>
                      <th className="text-left px-4 py-3">Secundario</th>
                      <th className="text-left px-4 py-3">Puntos</th>
                      <th className="text-left px-4 py-3">1er email</th>
                      <th className="text-left px-4 py-3">Secuencia</th>
                      <th className="text-left px-4 py-3">Última actividad</th>
                      <th className="text-left px-4 py-3">Mapa Completo</th>
                      <th className="text-left px-4 py-3">Fecha</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan={11} className="px-4 py-10 text-center text-white/40">
                          Sin leads todavía.
                        </td>
                      </tr>
                    )}
                    {filtered.map((l) => (
                      <tr key={l.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                        <td className="px-4 py-3 text-white">{l.name}</td>
                        <td className="px-4 py-3 text-white/70">{l.email}</td>
                        <td className="px-4 py-3 text-xs" data-testid={`lead-company-${l.id}`}>
                          {l.company_code ? (
                            <div className="flex flex-col gap-0.5 max-w-[160px]">
                              <span className="text-white/80 truncate" title={l.company_name}>{l.company_name}</span>
                              <span className="text-[9px] font-mono tracking-wider text-[#29BC9B]/80">{l.company_code}</span>
                            </div>
                          ) : (
                            <span className="text-white/30">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className="px-2 py-1 rounded-sm text-[10px] font-bold tracking-[0.15em]"
                            style={{
                              color: ARCHETYPE_COLOR[l.archetype],
                              background: `${ARCHETYPE_COLOR[l.archetype]}1A`,
                            }}
                          >
                            {ARCHETYPE_LABEL[l.archetype]}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-white/60 text-xs">
                          {l.secondary ? ARCHETYPE_LABEL[l.secondary] : "—"}
                        </td>
                        <td className="px-4 py-3 text-xs text-white/60">
                          {l.scores
                            ? `${l.scores.leon ?? 0}/${l.scores.lobo ?? 0}/${
                                l.scores.halcon ?? 0
                              }/${l.scores.delfin ?? 0}`
                            : "—"}
                        </td>
                        <td className="px-4 py-3 text-xs">
                          {l.email_status?.sent ? (
                            <span className="text-[#29BC9B]">enviado</span>
                          ) : l.email_status?.reason ? (
                            <span className="text-red-400/80" title={l.email_status.reason}>
                              falló
                            </span>
                          ) : (
                            <span className="text-white/30">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs" data-testid={`lead-seq-${l.id}`}>
                          <SequenceCell state={l.sequence_state} />
                        </td>
                        <td className="px-4 py-3 text-xs" data-testid={`lead-activity-${l.id}`}>
                          <ActivityCell
                            lastSentAt={l.sequence_state?.last_sent_at}
                            lastSubject={l.sequence_state?.last_subject}
                            lastOpenAt={l.last_open_at}
                            opens={l.opens}
                            clicks={l.clicks}
                          />
                        </td>
                        <td className="px-4 py-3 text-xs" data-testid={`lead-mapa-${l.id}`}>
                          <MapaInteresCell lead={l} />
                        </td>
                        <td className="px-4 py-3 text-xs text-white/40 tabular-nums">
                          {new Date(l.created_at).toLocaleString("es")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}

        {view === "sequences" && <EstrategiasPanel password={pwd} />}
        {view === "companies" && <EmpresasPanel password={pwd} onChange={() => tryLogin(pwd)} />}
        {view === "solicitudes" && <SolicitudesPanel password={pwd} />}
      </main>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="p-5 rounded-sm border border-white/8 bg-[#0A0A0A]">
      <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">{label}</div>
      <div className="font-display text-3xl" style={{ color: accent }}>
        {value}
      </div>
    </div>
  );
}

// --- Helpers for table cells ---
function relativeTime(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (isNaN(then)) return null;
  const diff = Date.now() - then;
  const m = Math.floor(diff / 60000);
  if (m < 1) return "ahora";
  if (m < 60) return `hace ${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `hace ${h}h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `hace ${d}d`;
  const mo = Math.floor(d / 30);
  return `hace ${mo}mo`;
}

function SequenceCell({ state }) {
  if (!state) return <span className="text-white/30">—</span>;
  const total = state.total || 0;
  const step = state.current_index || 0;
  if (state.unsubscribed) {
    return (
      <div className="flex flex-col gap-0.5">
        <span className="text-white/60 tabular-nums">{step}/{total}</span>
        <span className="text-[9px] uppercase tracking-[0.2em] text-red-400/80">baja</span>
      </div>
    );
  }
  if (state.completed) {
    return (
      <div className="flex flex-col gap-0.5">
        <span className="text-[#29BC9B] tabular-nums">{total}/{total}</span>
        <span className="text-[9px] uppercase tracking-[0.2em] text-[#29BC9B]/80">completada</span>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-white tabular-nums">{step}/{total}</span>
      <span className="text-[9px] uppercase tracking-[0.2em] text-white/40">activa</span>
    </div>
  );
}

function ActivityCell({ lastSentAt, lastSubject, lastOpenAt, opens, clicks }) {
  if (!lastSentAt && !lastOpenAt) return <span className="text-white/30">—</span>;
  return (
    <div className="flex flex-col gap-0.5 max-w-[220px]">
      {lastSentAt && (
        <span className="text-white/70 truncate" title={lastSubject || ""}>
          <span className="text-white/30">↗</span> {relativeTime(lastSentAt)}
          {lastSubject ? <span className="text-white/40"> · {lastSubject}</span> : null}
        </span>
      )}
      <span className="text-white/50">
        <span className="text-[#29BC9B]">●</span>{" "}
        {lastOpenAt ? `abierto ${relativeTime(lastOpenAt)}` : "sin abrir"}
        {opens ? <span className="text-white/30"> · {opens}o</span> : null}
        {clicks ? <span className="text-white/30"> · {clicks}c</span> : null}
      </span>
    </div>
  );
}

const ORIGIN_LABEL = {
  resultado_test: "test",
  email_3: "email #3",
  email_5: "email #5",
};

function MapaInteresCell({ lead }) {
  const clicked = Boolean(lead?.click_mapa_completo);
  const submitted = Boolean(lead?.solicito_mapa_completo);
  if (!clicked && !submitted) return <span className="text-white/30">—</span>;
  const origin = ORIGIN_LABEL[lead?.origen_interes_mapa] || lead?.origen_interes_mapa;
  const wa = (lead?.whatsapp_mapa || "").trim();
  const waHref = wa ? `https://wa.me/${wa.replace(/[^\d+]/g, "")}` : null;
  return (
    <div className="flex flex-col gap-0.5 max-w-[200px]">
      <div className="flex items-center gap-1.5">
        {submitted ? (
          <span className="text-[9px] font-bold tracking-[0.15em] uppercase text-[#29BC9B] bg-[#29BC9B]/15 px-1.5 py-0.5 rounded-sm">
            solicitó
          </span>
        ) : (
          <span className="text-[9px] font-bold tracking-[0.15em] uppercase text-white/60 bg-white/10 px-1.5 py-0.5 rounded-sm">
            click
          </span>
        )}
        {origin && <span className="text-[9px] uppercase tracking-[0.2em] text-white/40">{origin}</span>}
      </div>
      {wa && (
        waHref ? (
          <a
            href={waHref}
            target="_blank"
            rel="noreferrer"
            className="text-[#29BC9B] hover:underline text-xs truncate"
            title={wa}
          >
            {wa}
          </a>
        ) : (
          <span className="text-white/70 text-xs truncate" title={wa}>{wa}</span>
        )
      )}
      {(lead?.fecha_solicitud_mapa || lead?.fecha_click_mapa) && (
        <span className="text-[9px] text-white/30">
          {relativeTime(lead.fecha_solicitud_mapa || lead.fecha_click_mapa)}
        </span>
      )}
    </div>
  );
}

